import { BarcodeEvent, BarcodeType, ScannerConfig, KeyPressMetrics, PositionData } from '../types/barcode'

export class BarcodeScanner {
  private buffer: string[] = []
  private lastKeyTime: number = 0
  private config: ScannerConfig
  private isListening: boolean = false
  private keyPressIntervals: number[] = []
  private subscribers: Map<string, Set<(event: BarcodeEvent) => void>> = new Map()
  // Tracks whether we are currently in the middle of a scanner "burst"
  private isKeyPressed: boolean = false
  private scanTimeoutId: number | null = null

  // Regex pattern for position codes (supports "-" or "." as separators)
  private static readonly POSITION_PATTERN = /^([a-zA-Z0-9]{1,3})[-.]([a-zA-Z0-9]{1,3})[-.]([a-zA-Z0-9]{1,3})$/

  // Mapping of Czech keyboard top-row characters to digits (for HID scanners on CZ layout)
  private static readonly CZECH_DIGIT_MAP: Record<string, string> = {
    '+': '1',
    '=': '-',
    ě: '2',
    Ě: '2',
    š: '3',
    Š: '3',
    č: '4',
    Č: '4',
    ř: '5',
    Ř: '5',
    ž: '6',
    Ž: '6',
    ý: '7',
    Ý: '7',
    á: '8',
    Á: '8',
    í: '9',
    Í: '9',
    é: '0',
    É: '0',
  }

  constructor(config?: Partial<ScannerConfig>) {
    this.config = {
      timeToScan: 300,
      enableAudio: false,
      enableDebugMode: false,
      carriersConfig: this.getDefaultCarriersConfig(),
      ...config,
    }
  }

  private getDefaultCarriersConfig() {
    return [
      {
        name: 'GLS',
        pattern: /^(90|00)\d+$/,
        transform: (code: string) => code.slice(0, -1),
      },
      {
        name: 'PPL',
        pattern: /^[248]\d{10,}$/,
        transform: (code: string) => code.slice(0, -1),
      },
      {
        name: 'Zasilkovna',
        pattern: /^[ZY]\d+$/,
        transform: (code: string) => code.slice(1),
      },
      {
        name: 'DPD',
        pattern: /^%00/,
        transform: (code: string) => code.replace('%00', ''),
      },
    ]
  }

  start(): void {
    if (this.isListening) return

    this.isListening = true
    this.buffer = []
    this.lastKeyTime = Date.now()

    if (this.config.enableDebugMode) {
      console.log('BarcodeScanner: Started listening')
    }

    window.addEventListener('keydown', this.handleKeyDown)
  }

  stop(): void {
    if (!this.isListening) return

    this.isListening = false
    window.removeEventListener('keydown', this.handleKeyDown)

    if (this.config.enableDebugMode) {
      console.log('BarcodeScanner: Stopped listening')
    }
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    // Skip if user is typing in an input field
    const target = event.target as HTMLElement
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return
    }

    // Allow keybinds (Ctrl/Cmd/Alt + key) to pass through
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return
    }

    const currentTime = Date.now()
    const timeDiff = currentTime - this.lastKeyTime

    // For calibration
    if (timeDiff <= 200) {
      this.keyPressIntervals.push(timeDiff)
    }

    // If Enter is pressed and we already have a buffer, treat it as an explicit end-of-scan
    if (event.key === 'Enter' && this.buffer.length > 0) {
      event.preventDefault()
      this.processBuffer()
      this.isKeyPressed = false
      if (this.scanTimeoutId !== null) {
        clearTimeout(this.scanTimeoutId)
        this.scanTimeoutId = null
      }
      this.lastKeyTime = currentTime
      return
    }

    // Collect characters for potential scan
    if (event.key.length === 1) {
      event.preventDefault()
      this.buffer.push(event.key)

      // Start a timeout-based scan window on first key in a burst
      if (!this.isKeyPressed) {
        this.isKeyPressed = true

        if (this.scanTimeoutId !== null) {
          clearTimeout(this.scanTimeoutId)
        }

        this.scanTimeoutId = window.setTimeout(() => {
          // Only treat as a scan if we have a reasonably long code
          if (this.buffer.length >= 3) {
            this.processBuffer()
          } else {
            // Too short – discard
            this.buffer = []
          }
          this.isKeyPressed = false
          this.scanTimeoutId = null
        }, this.config.timeToScan)
      }
    }

    this.lastKeyTime = currentTime
  }

  private processBuffer(): void {
    const rawCode = this.buffer.join('')
    this.buffer = []

    // Ignore empty or too-short sequences (likely not real scans)
    if (rawCode.length < 3) return

    // Normalize potential Czech keyboard characters to digits before detection/processing
    const normalizedCode = this.normalizeCzechKeyboardInput(rawCode)

    const barcodeType = this.detectBarcodeType(normalizedCode)
    const processedCode = this.processBarcode(normalizedCode, barcodeType)

    const event: BarcodeEvent = {
      type: barcodeType,
      data: processedCode,
      timestamp: Date.now(),
      raw: rawCode,
    }

    if (this.config.enableDebugMode) {
      console.log('BarcodeScanner: Scanned', event)
    }

    console.log('🪄 emitting barcode from processBuffer', event)
    this.emit(event)

    if (this.config.enableAudio) {
      this.playBeep()
    }
  }

  private detectBarcodeType(code: string): BarcodeType {
    // Box / package barcodes should always behave as regular product barcodes,
    // so we must detect them before treating codes as POSITION / GS1 / QR.
    if (BarcodeScanner.isBoxBarcode(code)) {
      return 'BARCODE'
    }

    // Check if it's a position code (format: XXX-YYY-ZZZ)
    if (BarcodeScanner.POSITION_PATTERN.test(code)) {
      return 'POSITION'
    }

    // Check if it's a GS1 code (starts with specific prefixes)
    if (/^(\(01\)|01)\d{14}/.test(code)) {
      return 'GS1'
    }

    // Check if it's a QR code (typically contains URLs or structured data)
    if (code.includes('http') || code.includes('://') || code.includes('\n')) {
      return 'QR'
    }

    return 'BARCODE'
  }

  private processBarcode(code: string, type: BarcodeType): string {
    if (type === 'BARCODE') {
      // Apply carrier-specific transformations
      for (const carrier of this.config.carriersConfig) {
        if (carrier.pattern.test(code) && carrier.transform) {
          return carrier.transform(code)
        }
      }
    }

    return code
  }

  // Convert characters produced by Czech keyboard layout to their intended digits
  private normalizeCzechKeyboardInput(input: string): string {
    if (!input) return input
    let output = ''
    for (const ch of input) {
      output += BarcodeScanner.CZECH_DIGIT_MAP[ch] ?? ch
    }
    return output
  }

  private playBeep(): void {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)

    oscillator.frequency.value = 1000
    oscillator.type = 'sine'
    gainNode.gain.value = 0.1

    oscillator.start(audioContext.currentTime)
    oscillator.stop(audioContext.currentTime + 0.1)
  }

  subscribe(id: string, callback: (event: BarcodeEvent) => void): void {
    if (!this.subscribers.has(id)) {
      this.subscribers.set(id, new Set())
    }
    this.subscribers.get(id)!.add(callback)
  }

  unsubscribe(id: string, callback: (event: BarcodeEvent) => void): void {
    const callbacks = this.subscribers.get(id)
    if (callbacks) {
      callbacks.delete(callback)
      if (callbacks.size === 0) {
        this.subscribers.delete(id)
      }
    }
  }

  private emit(event: BarcodeEvent): void {
    this.subscribers.forEach(callbacks => {
      callbacks.forEach(callback => {
        try {
          callback(event)
        } catch (error) {
          console.error('BarcodeScanner: Error in subscriber', error)
        }
      })
    })
  }

  updateConfig(config: Partial<ScannerConfig>): void {
    this.config = { ...this.config, ...config }
    if (this.config.enableDebugMode) {
      console.log('BarcodeScanner: Config updated', this.config)
    }
  }

  getConfig(): ScannerConfig {
    return { ...this.config }
  }

  calibrate(): KeyPressMetrics {
    if (this.keyPressIntervals.length < 10) {
      throw new Error('Not enough data for calibration. Please scan more barcodes.')
    }

    const intervals = [...this.keyPressIntervals]
    const averageInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length
    const maxInterval = Math.max(...intervals)

    // Calculate suggested time to scan
    let suggestedTimeToScan: number
    if (5 * averageInterval < maxInterval) {
      suggestedTimeToScan = 5 * averageInterval
    } else {
      suggestedTimeToScan = maxInterval - averageInterval
    }

    // Apply a multiplier for safety margin
    suggestedTimeToScan = Math.floor(suggestedTimeToScan * 50)

    return {
      intervals,
      averageInterval,
      maxInterval,
      suggestedTimeToScan,
    }
  }

  resetCalibration(): void {
    this.keyPressIntervals = []
  }

  // Utility method to parse position barcodes
  static parsePositionCode(code: string): PositionData | null {
    const match = code.match(BarcodeScanner.POSITION_PATTERN)
    if (match) {
      return {
        position1: match[1],
        position2: match[2],
        position3: match[3],
      }
    }
    return null
  }

  // Utility for detecting box/package barcodes used on the frontend
  static isBoxBarcode(code: string): boolean {
    if (!code) return false

    const trimmed = code.trim()

    // Standard box codes: "box-123", "BOX-S-123", etc. (anything starting with "box-")
    if (/^box-.+$/i.test(trimmed)) return true

    // PR box codes: "pr-123", "PR-S-123", etc. (anything starting with "pr-")
    if (/^pr-.+$/i.test(trimmed)) return true

    // New box codes: "X100.BOX0105" etc.
    if (/^X100\.BOX\d+$/i.test(trimmed)) return true

    return false
  }
}
